'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import LocalStorageService from '../../../application/features/common/local-storage-service';
import { useAppSelector, useAppDispatch } from '../../../application/store/useStore';
import { useTranslation } from 'react-i18next';
import { LanguageCodeEnum } from '../../../application/common/enums/language';
import EngFlag from './icon-eng.png';
import NLFlag from './icon-de.png';
import { AddCircleOutline, ExpandLess, ExpandMore, Description, Settings, Business, People, LibraryBooks } from '@mui/icons-material';
import { getCompaniesAsync } from '../../../application/features/company/companyThunk';
import { getUsersAsync } from '../../../application/features/users/userThunk';
import { useCompanyContext } from '../../../application/context/CompanyContext';
import { AppBar, Toolbar, Button, MenuItem, Avatar, IconButton, Box, useTheme, Menu, Typography, alpha, useMediaQuery, Drawer, Autocomplete, TextField } from '@mui/material';
import logo from './logo.svg';
import { HambergerMenu, Logout } from 'iconsax-react';
import { NewProposalDialog } from '../../../ui/pages/proposals/new-proposal-dilaog';
import { toast } from 'react-toastify';
const LANGUAGES = [
    {
        code: 0,
        language: 'Dutch',
        ns: 'nl'
    },
    {
        code: 1,
        language: 'English',
        ns: 'en'
    }
];
export default function Header() {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const { currentUser } = useAppSelector((state) => state.auth);
    const { allCompanies } = useAppSelector((state) => state.company);
    const { allUsers } = useAppSelector((state) => state.users);
    const { instance } = useMsal();
    const navigate = useNavigate();
    const [newProposal, setNewProposal] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
    const isMenuOpen = Boolean(anchorEl);
    const isSettingsMenuOpen = Boolean(settingsAnchorEl);
    const isMdScreen = useMediaQuery('(min-width:900px)');
    const isSmallScreen = useMediaQuery('(max-width:900px)');
    const { selectedCompanyId, setSelectedCompanyId } = useCompanyContext();
    const [availableCompanies, setAvailableCompanies] = useState([]);
    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget); // Set anchor element
    };
    const handleMenuClose = () => {
        setAnchorEl(null); // Clear anchor element
    };
    const handleSettingsMenuOpen = (event) => {
        setSettingsAnchorEl(event.currentTarget);
    };
    const handleSettingsMenuClose = () => {
        setSettingsAnchorEl(null);
    };
    const canAccessSettings = () => {
        return currentUser?.role === 'SuperAdmin' || currentUser?.role === 'Admin';
    };
    const handleNewProposalClick = () => {
        if (!selectedCompanyId || selectedCompanyId === 'ALL') {
            toast.warning(t('companyRequired') || 'Please select a company before creating a proposal');
            return;
        }
        setNewProposal(true);
    };
    const handleNewProposalClose = () => {
        setNewProposal(false);
    };
    const [selectedLanguage, setSelectedLanguage] = useState('English');
    const [selectedIcon, setSelectedIcon] = useState(EngFlag);
    const getInitials = (name = '') => name
        .split(' ')
        .map((n) => n.charAt(0))
        .join('')
        .toUpperCase();
    useEffect(() => {
        if (!i18n.language && !localStorage.getItem('selectedFlag'))
            return;
        const lang = LANGUAGES.find((obj) => obj.ns === i18n.language);
        const storedFlag = localStorage.getItem('selectedFlag');
        if (lang) {
            setSelectedLanguage(lang.language);
        }
        else {
            setSelectedLanguage('EN'); // Default fallback
        }
        setSelectedIcon(storedFlag ?? EngFlag); // Use default flag if storedFlag is null/undefined
    }, []);
    // Fetch companies and users on mount
    useEffect(() => {
        dispatch(getCompaniesAsync());
        dispatch(getUsersAsync());
    }, [dispatch]);
    // Filter companies based on user role
    useEffect(() => {
        if (!currentUser || !allCompanies)
            return;
        if (currentUser.role === 'SuperAdmin') {
            // SuperAdmin sees all companies
            setAvailableCompanies(allCompanies);
        }
        else {
            // Admin and User roles see only their assigned companies
            const currentUserDetails = allUsers?.find((u) => u.email === currentUser.email);
            if (currentUserDetails?.companyIds && currentUserDetails.companyIds.length > 0) {
                const userCompanies = allCompanies.filter((company) => currentUserDetails.companyIds?.includes(company.id));
                setAvailableCompanies(userCompanies);
                // Auto-select the company if only one is assigned
                if (userCompanies.length === 1 && !selectedCompanyId) {
                    setSelectedCompanyId(userCompanies[0].id);
                }
            }
            else {
                setAvailableCompanies([]);
            }
        }
    }, [currentUser, allCompanies, allUsers, selectedCompanyId, setSelectedCompanyId]);
    const handleLanguageChange = (languageCode, icon) => {
        const selectedLanguage = LANGUAGES.find((obj) => obj.code === languageCode);
        if (selectedLanguage)
            setSelectedLanguage(selectedLanguage.language);
        setSelectedIcon(icon);
        localStorage.setItem('selectedFlag', icon);
        i18n.changeLanguage(selectedLanguage.ns);
        setAnchorEl(null);
    };
    // const handleMenuOpenChange = (e: any, data: any) => {
    //   setIsMenuOpen(data.open);
    // };
    const location = useLocation();
    const isActive = location.pathname === '/proposal';
    const handleTendersClick = () => {
        navigate('/tender');
    };
    const handleUserClick = () => {
        navigate('/users');
    };
    const handleRouteClick = (route) => {
        navigate(route);
    };
    const logout = () => {
        const currentAccount = instance.getActiveAccount();
        instance.logoutRedirect({
            account: currentAccount,
            postLogoutRedirectUri: '/',
            onRedirectNavigate: () => {
                LocalStorageService.removeMSAccessToken();
                LocalStorageService.removeAccessToken();
                LocalStorageService.removeRefreshToken();
                return true;
            }
        });
    };
    const [isDrawerOpen, setDrawerOpen] = useState(false);
    const handleDrawerToggle = () => {
        setDrawerOpen(!isDrawerOpen);
    };
    return (_jsxs(_Fragment, { children: [_jsx(AppBar, { position: 'sticky', color: 'default', elevation: 0, sx: {
                    boxShadow: theme.customShadows.card,
                    backgroundColor: theme.palette.background.default,
                    borderRadius: '16px',
                    paddingX: { xs: '0px', md: '40px' }
                }, children: _jsxs(Toolbar, { sx: { display: 'flex', justifyContent: 'space-between' }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', flexGrow: 1 }, children: [isSmallScreen && (_jsx(IconButton, { onClick: handleDrawerToggle, sx: { marginRight: 1 }, children: _jsx(HambergerMenu, {}) })), _jsx(Box, { onClick: () => handleRouteClick('/proposal'), sx: { cursor: 'pointer' }, children: _jsx("img", { src: logo, alt: 'VDK Aanbestedingen Logo', width: 74, height: 74 }) }), !isSmallScreen && (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, marginLeft: 4 }, children: [_jsx(Button, { variant: 'text', color: 'inherit', size: 'large', onClick: handleNewProposalClick, sx: { marginX: isMdScreen ? 2 : 0 }, startIcon: _jsx(AddCircleOutline, {}), children: _jsx(Typography, { variant: 'body2', children: t('newProposal') }) }), _jsx(Button, { variant: 'text', color: 'inherit', size: 'large', onClick: () => handleRouteClick('/proposal'), startIcon: _jsx(Description, {}), sx: {
                                                backgroundColor: location.pathname === '/proposal'
                                                    ? alpha(theme.palette.action.selected, 0.16)
                                                    : 'transparent',
                                                fontWeight: location.pathname === '/proposal' ? 600 : 400
                                            }, children: _jsx(Typography, { variant: 'body2', children: t('proposals') }) }), _jsx(Button, { variant: 'text', color: 'inherit', size: 'large', onClick: () => handleRouteClick('/knowledgebase'), startIcon: _jsx(LibraryBooks, {}), sx: {
                                                backgroundColor: location.pathname === '/knowledgebase'
                                                    ? alpha(theme.palette.action.selected, 0.16)
                                                    : 'transparent',
                                                fontWeight: location.pathname === '/knowledgebase' ? 600 : 400
                                            }, children: _jsx(Typography, { variant: 'body2', children: t('knowledgeBase') }) }), canAccessSettings() && (_jsxs(_Fragment, { children: [_jsx(Button, { variant: 'text', color: 'inherit', size: 'large', onClick: handleSettingsMenuOpen, startIcon: _jsx(Settings, {}), endIcon: isSettingsMenuOpen ? _jsx(ExpandLess, {}) : _jsx(ExpandMore, {}), sx: {
                                                        backgroundColor: location.pathname === '/company' || location.pathname === '/users'
                                                            ? alpha(theme.palette.action.selected, 0.16)
                                                            : 'transparent',
                                                        fontWeight: location.pathname === '/company' || location.pathname === '/users'
                                                            ? 600
                                                            : 400
                                                    }, children: _jsx(Typography, { variant: 'body2', children: t('settings') }) }), _jsxs(Menu, { anchorEl: settingsAnchorEl, open: isSettingsMenuOpen, onClose: handleSettingsMenuClose, anchorOrigin: {
                                                        vertical: 'bottom',
                                                        horizontal: 'left'
                                                    }, transformOrigin: {
                                                        vertical: 'top',
                                                        horizontal: 'left'
                                                    }, children: [_jsxs(MenuItem, { onClick: () => {
                                                                handleSettingsMenuClose();
                                                                handleRouteClick('/company');
                                                            }, sx: {
                                                                backgroundColor: location.pathname === '/company'
                                                                    ? alpha(theme.palette.action.selected, 0.08)
                                                                    : 'transparent'
                                                            }, children: [_jsx(Business, { sx: { mr: 1 } }), _jsx(Typography, { variant: 'body2', children: t('companies') })] }), _jsxs(MenuItem, { onClick: () => {
                                                                handleSettingsMenuClose();
                                                                handleRouteClick('/users');
                                                            }, sx: {
                                                                backgroundColor: location.pathname === '/users'
                                                                    ? alpha(theme.palette.action.selected, 0.08)
                                                                    : 'transparent'
                                                            }, children: [_jsx(People, { sx: { mr: 1 } }), _jsx(Typography, { variant: 'body2', children: t('users') })] })] })] }))] }))] }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 2 }, children: [!isSmallScreen && availableCompanies.length > 0 && (_jsx(Autocomplete, { options: currentUser?.role === 'SuperAdmin' || availableCompanies.length > 1
                                        ? [
                                            {
                                                id: 'ALL',
                                                name: t('selectAll'),
                                                isActive: true,
                                                email: '',
                                                address: '',
                                                domain: '',
                                                telephone: '',
                                                description: ''
                                            },
                                            ...availableCompanies
                                        ]
                                        : availableCompanies, getOptionLabel: (option) => option.name, value: selectedCompanyId === 'ALL'
                                        ? {
                                            id: 'ALL',
                                            name: t('selectAll'),
                                            isActive: true,
                                            email: '',
                                            address: '',
                                            domain: '',
                                            telephone: '',
                                            description: ''
                                        }
                                        : availableCompanies.find((c) => c.id === selectedCompanyId) || null, onChange: (_, newValue) => {
                                        setSelectedCompanyId(newValue?.id || null);
                                    }, sx: { minWidth: 200 }, renderInput: (params) => (_jsx(TextField, { ...params, label: t('selectCompany'), size: 'small', sx: {
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: theme.palette.background.paper
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: theme.palette.text.primary,
                                                fontWeight: 500
                                            },
                                            '& .MuiInputLabel-root.Mui-focused': {
                                                color: theme.palette.primary.main
                                            },
                                            '& .MuiOutlinedInput-input': {
                                                color: theme.palette.text.primary,
                                                fontWeight: 500
                                            },
                                            '& .MuiAutocomplete-input': {
                                                color: `${theme.palette.text.primary} !important`,
                                                fontWeight: 500
                                            }
                                        } })) })), _jsxs(Button, { variant: 'text', color: 'inherit', size: 'large', onClick: handleMenuOpen, endIcon: isMenuOpen ? _jsx(ExpandLess, {}) : _jsx(ExpandMore, {}), sx: { gap: 1, '& .MuiButton-endIcon': { margin: 0 } }, children: [_jsx("img", { src: selectedIcon, alt: 'flag', width: 24, height: 24 }), ' ', isMdScreen ? selectedLanguage : ''] }), _jsxs(Menu, { anchorEl: anchorEl, open: isMenuOpen, onClose: handleMenuClose, anchorOrigin: {
                                        vertical: 'bottom',
                                        horizontal: 'left'
                                    }, transformOrigin: {
                                        vertical: 'top',
                                        horizontal: 'left'
                                    }, children: [_jsxs(MenuItem, { onClick: () => handleLanguageChange(LanguageCodeEnum.EN, EngFlag), children: [_jsx("img", { src: EngFlag, alt: 'English Flag', width: 24, height: 24 }), _jsx(Typography, { sx: { marginLeft: 1 }, children: isMdScreen ? 'English' : '' })] }), _jsxs(MenuItem, { onClick: () => handleLanguageChange(LanguageCodeEnum.NL, NLFlag), children: [_jsx("img", { src: NLFlag, alt: 'Dutch Flag', width: 24, height: 24 }), _jsx(Typography, { sx: { marginLeft: 1 }, children: isMdScreen ? 'Dutch' : '' })] })] }), _jsx(Avatar, { variant: 'rounded', sx: { bgcolor: theme.palette.primary.main }, children: getInitials(currentUser?.name) }), !isSmallScreen && (_jsx(Button, { variant: 'text', size: 'large', startIcon: _jsx(Logout, {}), onClick: logout, color: 'error', children: t('logout') }))] })] }) }), _jsx(NewProposalDialog, { onClose: handleNewProposalClose, openProposalDialog: newProposal }), _jsx(Drawer, { anchor: 'right', open: isDrawerOpen, onClose: handleDrawerToggle, children: _jsxs(Box, { sx: {
                        width: 250,
                        padding: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        height: '100%'
                    }, children: [_jsx(Typography, { variant: 'h6', sx: { mb: 2 }, children: t('menuHeading') }), availableCompanies.length > 0 && (_jsx(Box, { sx: { mb: 2 }, children: _jsx(Autocomplete, { options: currentUser?.role === 'SuperAdmin' || availableCompanies.length > 2
                                    ? [
                                        {
                                            id: 'ALL',
                                            name: t('selectAll'),
                                            isActive: true,
                                            email: '',
                                            address: '',
                                            domain: '',
                                            telephone: '',
                                            description: ''
                                        },
                                        ...availableCompanies
                                    ]
                                    : availableCompanies, getOptionLabel: (option) => option.name, value: selectedCompanyId === 'ALL'
                                    ? {
                                        id: 'ALL',
                                        name: t('selectAll'),
                                        isActive: true,
                                        email: '',
                                        address: '',
                                        domain: '',
                                        telephone: '',
                                        description: ''
                                    }
                                    : availableCompanies.find((c) => c.id === selectedCompanyId) || null, onChange: (_, newValue) => {
                                    setSelectedCompanyId(newValue?.id || null);
                                }, renderInput: (params) => (_jsx(TextField, { ...params, label: t('selectCompany'), size: 'small' })) }) })), _jsx(Button, { fullWidth: true, variant: 'text', size: 'large', color: 'inherit', onClick: () => {
                                handleDrawerToggle();
                                handleRouteClick('/proposal');
                            }, startIcon: _jsx(Description, {}), sx: {
                                justifyContent: 'flex-start',
                                backgroundColor: location.pathname === '/proposal'
                                    ? alpha(theme.palette.action.selected, 0.16)
                                    : 'transparent',
                                fontWeight: location.pathname === '/proposal' ? 600 : 400
                            }, children: _jsx(Typography, { variant: 'body2', children: t('proposals') }) }), _jsx(Button, { fullWidth: true, variant: 'text', size: 'large', color: 'inherit', onClick: () => {
                                handleDrawerToggle();
                                handleRouteClick('/knowledgebase');
                            }, startIcon: _jsx(LibraryBooks, {}), sx: {
                                justifyContent: 'flex-start',
                                backgroundColor: location.pathname === '/knowledgebase'
                                    ? alpha(theme.palette.action.selected, 0.16)
                                    : 'transparent',
                                fontWeight: location.pathname === '/knowledgebase' ? 600 : 400
                            }, children: _jsx(Typography, { variant: 'body2', children: t('knowledgeBase') }) }), canAccessSettings() && (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: 'subtitle2', sx: { mt: 2, mb: 1, px: 2, color: 'text.secondary' }, children: t('settings') }), _jsx(Button, { fullWidth: true, variant: 'text', color: 'inherit', size: 'large', onClick: () => {
                                        handleDrawerToggle();
                                        handleRouteClick('/company');
                                    }, startIcon: _jsx(Business, {}), sx: {
                                        justifyContent: 'flex-start',
                                        backgroundColor: location.pathname === '/company'
                                            ? alpha(theme.palette.action.selected, 0.16)
                                            : 'transparent',
                                        fontWeight: location.pathname === '/company' ? 600 : 400,
                                        pl: 4
                                    }, children: _jsx(Typography, { variant: 'body2', children: t('companies') }) }), _jsx(Button, { fullWidth: true, variant: 'text', color: 'inherit', size: 'large', onClick: () => {
                                        handleDrawerToggle();
                                        handleRouteClick('/users');
                                    }, startIcon: _jsx(People, {}), sx: {
                                        justifyContent: 'flex-start',
                                        backgroundColor: location.pathname === '/users'
                                            ? alpha(theme.palette.action.selected, 0.16)
                                            : 'transparent',
                                        fontWeight: location.pathname === '/users' ? 600 : 400,
                                        pl: 4
                                    }, children: _jsx(Typography, { variant: 'body2', children: t('users') }) })] })), _jsx(Box, { sx: { marginTop: 'auto' }, children: _jsx(Button, { fullWidth: true, variant: 'text', startIcon: _jsx(Logout, {}), onClick: logout, color: 'error', children: t('logout') }) })] }) })] }));
}
